import dayjs, { Dayjs } from "dayjs";

export const ENABLED_DAY_COUNT = 5;
export const DEFAULT_WINDOW_SIZE = ENABLED_DAY_COUNT + 1;

export interface AllowedDateRange {
  start: Dayjs;
  end: Dayjs;
  allowedDates: Dayjs[];
  navigableDates: Dayjs[];
}

export const isWeekend = (date: Dayjs): boolean => {
  const day = date.day();
  return day === 0 || day === 6;
};

export const getPreviousWorkingDay = (date: Dayjs): Dayjs => {
  let prev = date.subtract(1, "day");
  while (isWeekend(prev)) {
    prev = prev.subtract(1, "day");
  }
  return prev.startOf("day");
};

export const getNextWorkingDays = (
  startDate: Dayjs,
  count: number,
  includeStart = false,
): Dayjs[] => {
  const workingDays: Dayjs[] = [];
  let currentDate = includeStart ? startDate : startDate.add(1, "day");

  while (workingDays.length < count) {
    if (!isWeekend(currentDate)) {
      workingDays.push(currentDate.startOf("day"));
    }
    currentDate = currentDate.add(1, "day");
  }

  return workingDays;
};

export const getAllowedDates = (): Dayjs[] => {
  const today = dayjs().startOf("day");

  if (isWeekend(today)) {
    return getNextWorkingDays(today, ENABLED_DAY_COUNT, false);
  }

  return getNextWorkingDays(today, ENABLED_DAY_COUNT, true);
};

export const getDefaultWindowStart = (): Dayjs => {
  return getPreviousWorkingDay(dayjs().startOf("day"));
};

export const getWeekdaysBetween = (start: Dayjs, end: Dayjs): Dayjs[] => {
  const days: Dayjs[] = [];
  let current = start.startOf("day");
  const endDate = end.startOf("day");

  while (current.isBefore(endDate) || current.isSame(endDate, "day")) {
    if (!isWeekend(current)) {
      days.push(current);
    }
    current = current.add(1, "day");
  }

  return days;
};

export const getNavigableDates = (): Dayjs[] => {
  const today = dayjs().startOf("day");
  const previousWorkingDay = getPreviousWorkingDay(today);
  const allowedDates = getAllowedDates();
  const lastAllowed = allowedDates[allowedDates.length - 1];
  const rangeStart = previousWorkingDay.day(1);
  const rangeEnd = lastAllowed.day(5);

  return getWeekdaysBetween(rangeStart, rangeEnd);
};

export const getDefaultWindowOffset = (navigableDates: Dayjs[]): number => {
  const defaultStart = getDefaultWindowStart();
  const index = navigableDates.findIndex((date) => date.isSame(defaultStart, "day"));
  return index >= 0 ? index : 0;
};

export const getAllowedDateRange = (): AllowedDateRange => {
  const allowedDates = getAllowedDates();
  const navigableDates = getNavigableDates();

  return {
    start: getDefaultWindowStart(),
    end: allowedDates[allowedDates.length - 1],
    allowedDates,
    navigableDates,
  };
};
